/**
 * @typedef {'intro'|'steps'|'bullets'|'important'|'where'|'figure'|'table'|'related'} GuideBlockType
 */

import {
  buildPlayerLevelGuideRows,
  buildProfessionLevelGuideRows,
} from '@/utils/formatLevelRewards';

const guideBase = `${process.env.BASE_URL}guide/`;

/** Имена файлов в mob_app/guide/ — реальные PNG/JPG с сайта. Пока пусто. */
export const guideScreenshotFiles = {
  nav: 'nav.png',
  catalog: 'catalog.png',
  match: 'match.png',
  profile: 'profile.png',
  inventory: 'inventory.png',
  exchange: 'exchange.png',
};

export function guideScreenshotUrl(key) {
  const file = guideScreenshotFiles[key];
  return file ? `${guideBase}${file}` : '';
}

export const GUIDE_LEVELS = [
  { id: 0, label: 'Старт' },
  { id: 1, label: 'Прогнозы' },
  { id: 2, label: 'Экономика' },
  { id: 3, label: 'Справочник' },
];

/** @type {Array<{slug:string,title:string,summary:string,level:number,blocks:Array}>} */
export const guideArticles = [
  {
    slug: 'start',
    title: 'С чего начать',
    summary: 'Первые шаги после регистрации и навигация по приложению.',
    level: 0,
    blocks: [
      {
        type: 'intro',
        text: 'Прогносяус — это прогнозы на спортивные события плюс игровая экономика: кошелёк, сундуки, банк, биржа и коллекция. Можно играть «только прогнозы», а остальное открывать постепенно.',
      },
      {
        type: 'important',
        title: 'Без регистрации',
        items: [
          'Ты можешь смотреть каталог событий, таблицы, рейтинги и эту инструкцию.',
          'Чтобы отправить прогноз, копить опыт и пользоваться кошельком — нужен аккаунт.',
          'На странице матча будет кнопка «Сделать прогноз» — она ведёт на вход.',
        ],
      },
      {
        type: 'steps',
        title: 'Быстрый старт',
        items: [
          'Зарегистрируйся — на кошельке появится стартовый бонус.',
          'Открой «События» → выбери турнир → ближайший матч.',
          'Заполни прогноз. При желании включи ставку 10 🪙 на исход.',
          'После матча зайди на карточку снова и нажми «Получить опыт».',
          'Загляни в Профиль → Богатство: уровень, кошелёк, ачивки.',
          'Если выпал сундук — открой его в Инвентаре (🎒 в меню).',
        ],
      },
      {
        type: 'where',
        path: 'Нижнее и верхнее меню',
        hint: 'События, профиль, инвентарь — сверху; банк, биржа, работа, казна — снизу.',
        screenshotKey: 'nav',
      },
      {
        type: 'bullets',
        title: 'Куда нажимать',
        items: [
          'Главная — сводка и новости.',
          'События — список турниров и матчей.',
          'Профиль — твой прогресс, банк, биржа, фарм.',
          'Инвентарь — сундуки и лут (нужен вход).',
          'Рейтинги — таблицы прогнозистов и капитала.',
          'Как играть — эта инструкция, доступна всем.',
        ],
      },
      {
        type: 'where',
        path: 'События',
        hint: 'Список турниров — отсюда открываются матчи.',
        screenshotKey: 'catalog',
      },
      {
        type: 'related',
        slugs: ['prognosis', 'money', 'inventory'],
      },
    ],
  },
  {
    slug: 'prognosis',
    title: 'Прогнозы и ставки',
    summary: 'Как заполнить прогноз, за что дают баллы и как работает ставка.',
    level: 1,
    blocks: [
      {
        type: 'intro',
        text: 'Ядро игры — угадать исход и детали матча. За это дают баллы в рейтинг, опыт для уровня и (опционально) выигрыш по ставке.',
      },
      {
        type: 'steps',
        title: 'Как отправить прогноз',
        items: [
          'Открой матч из каталога события.',
          'Укажи счёт и исход (п1 / ничья / п2).',
          'При желании заполни доп. метрики: владение, угловые, карточки…',
          'Включи ставку 10 🪙, если хочешь участвовать в пуле.',
          'Нажми «Отправить» до закрытия приёма прогнозов.',
        ],
      },
      {
        type: 'where',
        path: 'События → турнир → номер матча',
        hint: 'Форма прогноза: счёт, исход, метрики, кнопка «Отправить».',
        screenshotKey: 'match',
      },
      {
        type: 'bullets',
        title: 'Три разные награды',
        items: [
          'Баллы — за точность прогноза, идут в рейтинг турнира.',
          'Опыт (XP) — копится на уровень; забираешь кнопкой после матча.',
          'Прогнобаксы — только если ставил на исход и угадал.',
        ],
      },
      {
        type: 'important',
        title: 'Ставка на исход',
        items: [
          'Фиксированная ставка 10 🪙 с кошелька.',
          'Все ставки матча складываются в пул — выигрывают угадавшие исход.',
          'Выплата приходит после того, как в админке введён результат.',
          'Можно играть без ставок — только за баллы и опыт.',
        ],
      },
      {
        type: 'important',
        title: 'Важно',
        items: [
          'Бонусы фарма, банка и инвентаря на баллы прогноза не влияют.',
          'Чужие прогнозы до закрытия матча обычно скрыты.',
        ],
      },
      {
        type: 'related',
        slugs: ['xp-achievements', 'ratings', 'money'],
      },
    ],
  },
  {
    slug: 'xp-achievements',
    title: 'Опыт, уровни и ачивки',
    summary: 'Как расти в уровне и забирать награды за достижения.',
    level: 1,
    blocks: [
      {
        type: 'intro',
        text: 'Опыт открывает уровни и награды. Ачивки — отдельные цели с бонусами: сундуки, деньги, значки прогресса.',
      },
      {
        type: 'steps',
        title: 'Опыт',
        items: [
          'XP начисляется за матчи (после результата) и за ачивки.',
          'На карточке завершённого матча нажми «Получить опыт», если кнопка активна (с Premium — забирается автоматически).',
          'В профиле виден текущий уровень и прогресс до следующего.',
          'За переход на новый уровень — прогнобаксы, рублиусы и сундук за уровень (⭐).',
          'Каждый 5-й уровень игрока — усиленная награда (★), лицензия на профессию и всё то же.',
          'На 10-м и далее каждые 10 уровней — дополнительно лицензия на усадьбу (на 10-м — обе лицензии).',
        ],
      },
      {
        type: 'table',
        title: 'Уровень игрока — опыт и награды',
        hint: 'Порог XP — суммарный опыт с начала. Награда при переходе на уровень. Ур. 5: +200 🪙, +20 💎, 1 ⭐сундук, лицензия на профессию. Ур. 10: +400 🪙, +40 💎, обе лицензии.',
        headers: ['Уровень', 'Нужно XP', 'Награда'],
        rows: buildPlayerLevelGuideRows(15),
      },
      {
        type: 'important',
        items: [
          'Баннер в шапке («Уровень N») — только для уровня игрока, не для профессий на фарме.',
        ],
      },
      {
        type: 'steps',
        title: 'Ачивки',
        items: [
          'Открой Профиль → раздел ачивок.',
          'Выполненные цели подсвечиваются — нажми «Забрать», чтобы получить награду.',
          'Есть ачивки за прогнозы, ЧМ, биржу, альбом, рецепты и другое.',
        ],
      },
      {
        type: 'related',
        slugs: ['prognosis', 'inventory', 'album'],
      },
    ],
  },
  {
    slug: 'ratings',
    title: 'Рейтинги',
    summary: 'Таблицы прогнозистов, сборники и рейтинг капитала.',
    level: 1,
    blocks: [
      {
        type: 'intro',
        text: 'Рейтинги показывают, кто лучше угадывает и кто богаче в игровой экономике. Большинство таблиц доступны без входа.',
      },
      {
        type: 'bullets',
        items: [
          'Общий рейтинг турнира — по сумме баллов за прогнозы.',
          'Можно переключать активные и прошедшие события.',
          'Сборники — подборки участников (открытые, закрытые, приватные).',
          'Рейтинг капитала — богатые, бедные или с незабранным XP.',
        ],
      },
      {
        type: 'important',
        items: [
          'В рейтинг прогнозов идут только баллы за угадывание, не прогнобаксы.',
        ],
      },
      {
        type: 'related',
        slugs: ['prognosis', 'money'],
      },
    ],
  },
  {
    slug: 'money',
    title: 'Прогнобаксы и рублиусы',
    summary: 'Две валюты: откуда берутся и куда тратятся.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Прогнобаксы (🪙) — основная игровая валюта. Рублиусы (💎) — премиум для особых покупок.',
      },
      {
        type: 'bullets',
        title: 'Прогнобаксы 🪙',
        items: [
          'Стартовый бонус при регистрации.',
          'Выигрыш по ставкам на исход матча.',
          'Продажа на бирже, комиссионка банка, награды за уровень и ачивки.',
          'Траты: ставки, биржа, вклады и займы, лавка казны, гос. вклад.',
        ],
      },
      {
        type: 'bullets',
        title: 'Рублиусы 💎',
        items: [
          'Премиум-валюта, в основном из лавки и событий.',
          'Покупка в казне (премиум, особые товары).',
          'На бирже рублиусы не торгуются.',
        ],
      },
      {
        type: 'where',
        path: 'Профиль → Богатство',
        hint: 'Кошелёк 🪙, рублиусы 💎, уровень и сундуки.',
        screenshotKey: 'profile',
      },
      {
        type: 'related',
        slugs: ['bank', 'exchange', 'treasury'],
      },
    ],
  },
  {
    slug: 'inventory',
    title: 'Инвентарь и сундуки',
    summary: 'Что хранится в рюкзаке и как открывать награды.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Инвентарь — всё, что можно открыть, вклеить, продать или использовать в крафте. Нужна авторизация.',
      },
      {
        type: 'bullets',
        title: 'Что бывает',
        items: [
          'Сундуки ЧМ-26 — лут после матчей и ачивок.',
          'Сувениры: вымпелы, шарфы, сборные карточки.',
          'Банки XP — распаковываешь за опыт.',
          'Сертификаты профессий — +1 слот на фарме (каждый 5-й уровень игрока и из сундуков).',
          'Сертификаты на усадьбу — участок для постройки (каждый 10-й уровень игрока и из сундуков).',
          'Рецепты и материалы — для крафта на работе.',
        ],
      },
      {
        type: 'steps',
        title: 'Сундуки',
        items: [
          'Открой 🎒 Инвентарь в меню.',
          'Нажми на сундук — «Открыть» или «Открыть все».',
          'Лут попадёт в соответствующие вкладки.',
          'Журнал открытий — кнопка в шапке инвентаря.',
        ],
      },
      {
        type: 'where',
        path: '🎒 Инвентарь',
        hint: 'Вкладки по типам предметов, кнопка «Журнал» открытий.',
        screenshotKey: 'inventory',
      },
      {
        type: 'related',
        slugs: ['album', 'exchange', 'farm'],
      },
    ],
  },
  {
    slug: 'album',
    title: 'Альбом коллекции ЧМ-26',
    summary: 'Вклейка карточек, крафт и дубли.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Альбом — коллекция команд ЧМ. Собираешь оригиналы и сборные карточки, вклеиваешь в сетку и получаешь мега-ачивки.',
      },
      {
        type: 'steps',
        title: 'Как собирать',
        items: [
          'Добывай сувениры из сундуков или покупай на бирже.',
          'В Профиле открой раздел альбома / коллекции.',
          'Вклей оригинал или сборную в свободную ячейку.',
          '«Добавить оригиналы» — массовая вклейка подходящих карточек.',
          'Сборную можно скрафтить на фарме, если выучен рецепт.',
        ],
      },
      {
        type: 'bullets',
        title: 'Дубли',
        items: [
          'Если команда уже в альбоме — лишние копии можно продать.',
          'Биржа → Продать → Сувениры → «Продать дубли на биржу» или в комиссионку.',
          'Продаётся по номиналу автоматически.',
        ],
      },
      {
        type: 'important',
        items: [
          'Мега-ачивки за 16, 32 и 48 вклеенных слотов — смотри в ачивках.',
        ],
      },
      {
        type: 'related',
        slugs: ['inventory', 'farm', 'exchange'],
      },
    ],
  },
  {
    slug: 'bank',
    title: 'Банк и займы',
    summary: 'Вклады, займы и свой банк игрока.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Банки игроков принимают вклады и выдают займы. Можно пользоваться чужим банком или открыть свой.',
      },
      {
        type: 'bullets',
        title: 'Вклад и займ (у чужого банка)',
        items: [
          'Срок — 5 матчей выбранного турнира.',
          'Вклад от 100 🪙, займ фиксированно 50 🪙.',
          'Проценты начисляются по ходу турнира.',
          'Отменить можно до первого тика, если деньги ещё «лежат целиком».',
          'Займ можно погасить досрочно — с выплатой всех процентов.',
        ],
      },
      {
        type: 'bullets',
        title: 'Свой банк',
        items: [
          'Открывается за крупную сумму с резервом ликвидности.',
          'Клиенты кладут вклады — ты платишь проценты.',
          'Можно включить комиссионку: игроки сдают лут, банк выставляет на бирже.',
          'Комиссионные лоты не занимают твой лимит личных лотов.',
        ],
      },
      {
        type: 'important',
        title: 'Гос. вклад поддержки',
        items: [
          '500 🪙 в ликвидность банка на 5 туров.',
          'Проценты идут в казну, не на личный кошелёк.',
        ],
      },
      {
        type: 'related',
        slugs: ['money', 'exchange', 'treasury'],
      },
    ],
  },
  {
    slug: 'treasury',
    title: 'Казна',
    summary: 'Лавка государства и гос. заказы.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Казна — NPC-магазин и заказы от государства. Покупаешь сундуки и премиум, смотришь сводку по экономике.',
      },
      {
        type: 'bullets',
        items: [
          'Лавка: сундуки ЧМ, премиум за рублиусы.',
          'Гос. заказы на фарм — оплата за работу на казну.',
          'Статистика биржи и операций — для ориентира по рынку.',
        ],
      },
      {
        type: 'related',
        slugs: ['money', 'farm', 'exchange'],
      },
    ],
  },
  {
    slug: 'exchange',
    title: 'Биржа',
    summary: 'Покупка, продажа, комиссионка и биржа труда.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Биржа — торговля между игроками. Цены привязаны к номиналу предмета, есть лимит активных лотов.',
      },
      {
        type: 'bullets',
        title: 'Вкладки',
        items: [
          'Каталог — купить у других.',
          'Продать — выставить свой лут (категории: сундуки, сувениры, рецепты…).',
          'Мои лоты — снять с продажи.',
          'Работы — заказы на фарм/крафт от игроков.',
          'История — твои сделки.',
        ],
      },
      {
        type: 'steps',
        title: 'Продать предмет',
        items: [
          'Продать → выбери категорию → укажи количество и цену (от номинала).',
          '«Выставить» — лот на бирже на несколько дней.',
          '«В комиссионку» — сразу ~80% номинала, отменить нельзя.',
        ],
      },
      {
        type: 'important',
        title: 'Дубли сувениров',
        items: [
          'Продать → Сувениры — кнопка массовой продажи лишних дублей по номиналу.',
        ],
      },
      {
        type: 'where',
        path: 'Биржа (нижнее меню) или Профиль → Богатство → Биржа',
        hint: 'Вкладки Каталог / Продать / Мои лоты / Работы.',
        screenshotKey: 'exchange',
      },
      {
        type: 'related',
        slugs: ['money', 'inventory', 'farm'],
      },
    ],
  },
  {
    slug: 'farm',
    title: 'Фарм и профессии',
    summary: 'Работа на делянке, крафт и сертификаты.',
    level: 2,
    blocks: [
      {
        type: 'intro',
        text: 'Между матчами можно «работать»: добывать ресурсы, крафтить сборные для альбома или выполнять заказы.',
      },
      {
        type: 'steps',
        title: 'Как начать',
        items: [
          'Профиль → Богатство → вкладка «Работа» (или «Работа» в нижнем меню).',
          'Выбери до нескольких профессий (слоты ограничены).',
          'Запусти смену: для себя (лут в инвентарь) или на казну (фикс. оплата).',
          'Дождись таймера и забери результат.',
        ],
      },
      {
        type: 'bullets',
        items: [
          'Сертификат профессии из инвентаря даёт +1 слот.',
          'Рецепт альбома выучивается отдельно — потом крафт на фарме.',
          'Заказы других игроков — на бирже, вкладка «Работы».',
          'Уровень профессии не выше уровня игрока — качаемся прогнозами и банками XP.',
          'Награда за уровень профессии показывается после смены во вкладке «Запуски» (не в шапке).',
        ],
      },
      {
        type: 'table',
        title: 'Уровень профессии — опыт и награды',
        hint: 'Та же шкала XP, что у игрока. Ресурс в колонке «Награда» — материал вашей профессии (у лесоруба — бревна). Ур. 5: +80 🪙, +8 💎, +5 рес., 1 сундук проф.',
        headers: ['Уровень', 'Нужно XP', 'Награда'],
        rows: buildProfessionLevelGuideRows(10),
      },
      {
        type: 'related',
        slugs: ['inventory', 'album', 'exchange'],
      },
    ],
  },
  {
    slug: 'faq-extra',
    title: 'Частые вопросы',
    summary: 'Короткие ответы на типичные затыки.',
    level: 3,
    blocks: [
      {
        type: 'bullets',
        title: 'Прогнозы и деньги',
        items: [
          'Поставил прогноз со ставкой — деньги придут после результата матча, не сразу.',
          'Не забудь «Получить опыт» на карточке — XP сам не прилетает.',
          'Ставка списывается при отправке прогноза, если включена.',
        ],
      },
      {
        type: 'bullets',
        title: 'Биржа',
        items: [
          '«Достигнут лимит лотов» — сними старые в «Мои лоты» или сдай в комиссионку.',
          'Комиссионные лоты банка не считаются в твой лимит, если ты владелец банка.',
          'Цена не может быть ниже номинала предмета.',
        ],
      },
      {
        type: 'bullets',
        title: 'Альбом и инвентарь',
        items: [
          'Не вклеивается — возможно, нужна сборная, а у тебя только оригинал (или наоборот).',
          'Дубль после вклейки команды — продай на бирже в разделе сувениров.',
        ],
      },
      {
        type: 'bullets',
        title: 'Глоссарий',
        items: [
          'Номинал — базовая цена предмета на бирже.',
          'Комиссионка — сдача лутa банку за мгновенную выплату.',
          'Палета — максимум штук в одном лоте.',
          'Тик банка — начисление процентов после очередного матча турнира.',
          'Паримутуель — пул ставок делится между угадавшими исход.',
        ],
      },
      {
        type: 'related',
        slugs: ['start', 'prognosis', 'exchange'],
      },
    ],
  },
];

export function getGuideArticle(slug) {
  return guideArticles.find((item) => item.slug === slug) || null;
}

export function getGuideArticlesByLevel(level) {
  return guideArticles.filter((item) => item.level === level);
}
